/* ==========================================================================
   FILE: api/queue.js
   IAMONEAI - Offline Request Queue
   Stores requests when offline, syncs when back online
   ========================================================================== */

// ==========================================================================
// CONFIGURATION
// ==========================================================================

const QUEUE_CONFIG = {
  storageKey: 'iamoneai_offline_queue',
  maxItems: 100,
  priorities: {
    high: 1,
    normal: 2,
    low: 3,
  },
};

// ==========================================================================
// QUEUE STATE
// ==========================================================================

let queueItems = [];
let isProcessing = false;
let flushCallback = null;

// ==========================================================================
// LOAD FROM STORAGE
// ==========================================================================

function loadQueue() {
  try {
    const stored = localStorage.getItem(QUEUE_CONFIG.storageKey);
    if (stored) {
      queueItems = JSON.parse(stored);
    }
  } catch (err) {
    console.warn('Failed to load offline queue:', err);
    queueItems = [];
  }
}

// ==========================================================================
// SAVE TO STORAGE
// ==========================================================================

function saveQueue() {
  try {
    localStorage.setItem(QUEUE_CONFIG.storageKey, JSON.stringify(queueItems));
  } catch (err) {
    console.warn('Failed to save offline queue:', err);
  }
}

// ==========================================================================
// QUEUE METHODS
// ==========================================================================

const queue = {
  /**
   * Add request to queue
   * @param {Object} request - Request to queue
   * @param {string} request.endpoint - API endpoint
   * @param {string} request.method - HTTP method
   * @param {Object} request.body - Request body
   * @param {string} request.priority - Priority level
   * @returns {string} Queue item ID
   */
  add(request) {
    const id = `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const item = {
      id,
      ...request,
      priority: request.priority || 'normal',
      timestamp: Date.now(),
      attempts: 0,
    };

    // Enforce max items (remove oldest low priority)
    if (queueItems.length >= QUEUE_CONFIG.maxItems) {
      queueItems.sort((a, b) => {
        const priorityDiff = QUEUE_CONFIG.priorities[b.priority] - QUEUE_CONFIG.priorities[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return a.timestamp - b.timestamp;
      });
      queueItems.pop();
    }

    queueItems.push(item);
    saveQueue();

    return id;
  },

  /**
   * Remove item from queue
   * @param {string} id - Queue item ID
   */
  remove(id) {
    queueItems = queueItems.filter(item => item.id !== id);
    saveQueue();
  },

  /**
   * Get all queued items
   * @returns {Array} Queue items sorted by priority
   */
  getAll() {
    return [...queueItems].sort((a, b) => {
      const priorityDiff = QUEUE_CONFIG.priorities[a.priority] - QUEUE_CONFIG.priorities[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return a.timestamp - b.timestamp;
    });
  },

  /**
   * Get queue size
   * @returns {number}
   */
  size() {
    return queueItems.length;
  },

  /**
   * Check if queue has items
   * @returns {boolean}
   */
  hasItems() {
    return queueItems.length > 0;
  },

  /**
   * Clear all queued items
   */
  clear() {
    queueItems = [];
    saveQueue();
  },

  /**
   * Set callback for when flush needs to execute request
   * @param {Function} callback - (request) => Promise
   */
  setFlushCallback(callback) {
    flushCallback = callback;
  },

  /**
   * Flush queue - process all pending requests
   * @returns {Promise<Object>} Results summary
   */
  async flush() {
    if (isProcessing || queueItems.length === 0) {
      return { processed: 0, failed: 0 };
    }

    if (!flushCallback) {
      console.warn('No flush callback set for offline queue');
      return { processed: 0, failed: 0, error: 'No callback' };
    }

    isProcessing = true;
    const results = { processed: 0, failed: 0, errors: [] };

    // Get items sorted by priority
    const items = this.getAll();

    for (const item of items) {
      try {
        await flushCallback({
          endpoint: item.endpoint,
          method: item.method,
          body: item.body,
        });

        // Success - remove from queue
        this.remove(item.id);
        results.processed++;

      } catch (err) {
        item.attempts++;
        
        // Max 3 attempts
        if (item.attempts >= 3) {
          this.remove(item.id);
          results.failed++;
          results.errors.push({
            id: item.id,
            endpoint: item.endpoint,
            error: err.message,
          });
        } else {
          saveQueue();
        }
      }
    }

    isProcessing = false;
    return results;
  },

  /**
   * Get queue statistics
   * @returns {Object}
   */
  stats() {
    const byPriority = { high: 0, normal: 0, low: 0 };
    queueItems.forEach(item => {
      byPriority[item.priority] = (byPriority[item.priority] || 0) + 1;
    });

    return {
      total: queueItems.length,
      byPriority,
      isProcessing,
      oldestTimestamp: queueItems.length > 0 
        ? Math.min(...queueItems.map(i => i.timestamp))
        : null,
    };
  },
};

// ==========================================================================
// INITIALIZE
// ==========================================================================

// Load queue from storage on module load
if (typeof window !== 'undefined') {
  loadQueue();
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export { queue, QUEUE_CONFIG };
export default queue;